import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import {
  IconUser,
  InlineTextButton,
  LanguageSelect,
  LinkedLogo,
  LinkTabNavHorizontal,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
} from '../../../../components';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { USER_TYPE } from '../../../../constants/user.constants';
import { createCategoryForUrl, createSlug } from '../../../../util/urlHelpers';
import { LISTING_STATE_PUBLISHED } from '../../../../util/types';
import {
  getSubscriptionDetailsData,
  shouldRenderListingCreationLinks,
} from '../../../../util/userHelpers';
import GeneralMenu from '../GeneralMenu/GeneralMenu';
import { isEqual } from 'lodash';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';


import css from './TopbarDesktop.module.css';

const Navigation = props => {
  const { intl, currentPage } = props;
  return (
    <LinkTabNavHorizontal
      className={css.tabnav}
      tabClassName={css.tab}
      selectedClassName={css.tabSelected}
      skin="light"
      tabs={[
        {
          text: intl.formatMessage({
            id: 'Subheader.marketplace',
          }),
          linkProps: {
            name: 'SearchPage',
            to: { search: 'status=forSale' },
          },
          selected: currentPage === 'SearchPage',
        },
        {
          text: intl.formatMessage({
            id: 'Subheader.sailingNews',
          }),
          linkProps: {
            href: 'https://www.vaarmarkt.nl/nieuws-en-opinie/',
            target: '_self',
          },
        },
        {
          text: intl.formatMessage({
            id: 'Subheader.sailingGuide',
          }),
          linkProps: {
            href: 'https://www.vaarmarkt.nl/vaargids',
            target: '_self',
          },
        },
        {
          text: intl.formatMessage({
            id: 'Subheader.partners',
          }),
          linkProps: {
            href: 'https://www.vaarmarkt.nl/vaarpartners',
            target: '_self',
          },
        },
      ]}
    />
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.profileMenuLabel}>
      <IconUser className={css.iconUser} />
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: 'all' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, currentUserHasListings }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  // One overview for every conversation, so the entry point no longer depends on
  // whether the user has listings.
  const inboxTab = 'all';

  return (
    <Menu className={css.profileMenu}>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <IconUser className={css.iconUser} />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.inbox" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
            to={{ search: 'status=all' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="FavoriteListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoriteListingsPage'))}
            name="FavoriteListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.savedListings" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const ListingCreationMenu = ({ currentPage, currentUser, singleUserListing }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { lang, ...params } = useParams();
  const currentPageClass = (page, pageParams) => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return (currentPage === page && (!pageParams || isEqual(params, pageParams))) ||
      isAccountSettingsPage
      ? css.currentPage
      : null;
  };
  const { metadata, publicData } = currentUser?.attributes?.profile || {};
  const { userType } = publicData || {};
  const { subscription } = metadata || {};

  const { title, publicData: listingPublicData, state: listingState } =
    singleUserListing?.attributes || {};
  const { category } = listingPublicData || {};

  const isPrivateUser = userType === USER_TYPE.PRIVATE_SELLER;
  const { isSubscriptionActive } = getSubscriptionDetailsData(subscription);

  const isPublished = listingState === LISTING_STATE_PUBLISHED;
  const slug = isPrivateUser && title && createSlug(title);
  const categorySlug = isPrivateUser && createCategoryForUrl(category);
  const listingId = isPrivateUser && singleUserListing?.id?.uuid;
  const shouldRenderListingCreation = shouldRenderListingCreationLinks({
    listing: singleUserListing,
    isPrivateUser,
    isSubscriptionActive,
  });

  if (!shouldRenderListingCreation) {
    return null;
  }

  // Unauthenticated users get a direct link to signup (private seller only),
  // with `from` state so they land on the listing creation flow after auth.
  if (!currentUser) {
    return (
      <NamedLink
        className={classNames(css.profileMenuLabel, css.placeListing)}
        name="LoginPage"
      >
        <FormattedMessage id="TopbarDesktop.addAListing" />
      </NamedLink>
    );
  }

  // Private and business sellers now start in the same place. Private sellers used to
  // be sent to the duration/pay screen on a placeholder listing id, but payment is a
  // step *inside* the wizard now — there is nothing to pay for until the category step
  // has created the draft.
  if (isPrivateUser || userType) {
    return (
      <NamedLink
        className={classNames(css.profileMenuLabel, css.placeListing)}
        name="NewListingPage"
      >
        <FormattedMessage id="TopbarDesktop.addAListing" />
      </NamedLink>
    );
  }

  const links = [
    ...(!userType
      ? [
          {
            name: 'ProfileSettingsPage',
            label: 'TopbarDesktop.finishYourProfile',
          },
        ]
      : []),
  ].filter(Boolean);

  return (
    <Menu isOpen={isOpen} onToggleActive={setIsOpen} className={css.placeListingWrapper}>
      <MenuLabel
        className={classNames(css.profileMenuLabel, css.placeListing)}
        isOpenClassName={css.profileMenuIsOpen}
      >
        <FormattedMessage id="TopbarDesktop.addAListing" />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {links.map(item => (
          <MenuItem key={item.label}>
            <NamedLink
              className={classNames(
                css.menuLink,
                css.menuLinkListingCreation,
                currentPageClass(item.name, item.params)
              )}
              name={item.name}
              params={item.params}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id={item.label} />
            </NamedLink>
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.currentUserHasListings
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    singleUserListing,
    onManageDisableScrolling,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const listingCreationMenuMaybe = (
    <ListingCreationMenu
      currentPage={currentPage}
      currentUser={currentUser}
      singleUserListing={singleUserListing}
    />
  );

  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const spacer = (
    <div className={classNames(css.spacer, { [css.takeAvailableSpace]: giveSpaceForSearch })} />
  );

  const hamburgerMenu = (
    <GeneralMenu
      className={classNames(css.hamburgerMenu, css.topbarLink)}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      <Navigation intl={intl} currentPage={currentPage} />
      <div className={css.rightSide}>
        <LanguageSelect />
        {listingCreationMenuMaybe}
        {profileMenuMaybe}
        {loginLinkMaybe}
        {hamburgerMenu}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
